import { AvailabilityService } from '../services/availability.service'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { Event } from '../domain/Event'
import { EMPTY, Observable, Subject, catchError, combineLatest, distinctUntilChanged, filter, map, of, shareReplay, switchMap, take, takeUntil, throwError } from 'rxjs'
import { Venue } from '../domain/Venue'
import { adaptStringToDate } from '../utilities/helper'
import { environment } from '../../environments/environment'
import { BookingSlotTimeGroup } from '../domain/Availability'
import { ActivatedRoute, Router } from '@angular/router'
import { isVenueNotInServiceError } from '../services/venue-not-in-service-error'
import { richTextPlainText, truncateRichTextHtml } from '../utilities/rich-text'

@Component({
    selector: 'app-event-booking-page',
    templateUrl: './event-booking-page.component.html',
    styleUrls: ['./event-booking-page.component.sass'],
})
export class EventBookingPageComponent implements OnInit, OnDestroy {

    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string
    @Input() eventId!: string
    @Input({ transform: adaptStringToDate }) dateTime?: Date

    event$: Observable<Event | null> = of(null)
    venue$: Observable<Venue | null> = of(null)
    mapUrl$: Observable<string | null> = of(null)
    directionsUrl$: Observable<string | null> = of(null)
    eventBookingSlotGroups$: Observable<BookingSlotTimeGroup[]> = of([])
    modalIdealTime$: Observable<Date | null> = of(null)
    activeDateTime$: Observable<Date | null> = of(null)
    eventDateOptions$: Observable<Date[]> = of([])
    eventDateOptions: Date[] = []
    marketingUrl = environment.marketingUrl
    descriptionExpanded = false
    descriptionCharacterLimit = 600
    protected readonly richTextPlainText = richTextPlainText
    protected readonly truncateRichTextHtml = truncateRichTextHtml
    missingEventId = false
    venueNotInService = false

    private eventDateOptionLimit = 10
    private onDestroy$ = new Subject<void>()

    constructor(
        private availabilityService: AvailabilityService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    ngOnInit() {
        if (!this.eventId) {
            this.missingEventId = true
            return
        }

        const requestedDateTime$ = this.route.queryParamMap.pipe(
            map(params => adaptStringToDate(params.get('dateTime') ?? '') ?? null),
            distinctUntilChanged((a, b) => this.sameDateTime(a, b)),
            shareReplay(1)
        )

        const initialAvailability$ = requestedDateTime$.pipe(
            map(date => date ?? new Date()),
            distinctUntilChanged((a, b) => this.sameLocalDate(a, b)),
            switchMap(date => this.getAvailabilityForDate(date).pipe(
                map(availability => ({ availability, date }))
            )),
            takeUntil(this.onDestroy$),
            shareReplay(1)
        )

        const initialEvent$ = initialAvailability$.pipe(
            map(({ availability }) => availability.venue.eventWithId(this.eventId)),
            shareReplay(1)
        )

        const upcomingEventDateOptions$ = initialEvent$.pipe(
            map(event => event?.recurrence.upcomingDateTimes(new Date(), this.eventDateOptionLimit) ?? []),
            shareReplay(1)
        )

        this.activeDateTime$ = combineLatest([
            requestedDateTime$,
            initialEvent$,
        ]).pipe(
            map(([requestedDateTime, event]) => {
                return this.resolveActiveDateTime(requestedDateTime, event, new Date())
            }),
            distinctUntilChanged((a, b) => this.sameDateTime(a, b)),
            shareReplay(1)
        )

        this.eventDateOptions$ = combineLatest([
            upcomingEventDateOptions$,
            this.activeDateTime$,
        ]).pipe(
            map(([options, activeDateTime]) => this.includeActiveDateTimeOption(options, activeDateTime)),
            shareReplay(1)
        )

        this.eventDateOptions$.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(options => {
            this.eventDateOptions = options
        })

        combineLatest([
            requestedDateTime$,
            this.activeDateTime$,
        ]).pipe(
            filter(([requestedDateTime, activeDateTime]) => {
                return activeDateTime !== null && !this.sameDateTime(requestedDateTime, activeDateTime)
            }),
            take(1),
            takeUntil(this.onDestroy$)
        ).subscribe(([, activeDateTime]) => {
            this.updateDateTimeQueryParam(activeDateTime!, true)
        })

        const availability$ = combineLatest([
            initialAvailability$,
            this.activeDateTime$,
        ]).pipe(
            switchMap(([initialAvailability, activeDateTime]) => {
                if (!activeDateTime || this.sameLocalDate(initialAvailability.date, activeDateTime)) {
                    return of(initialAvailability.availability)
                }
                return this.getAvailabilityForDate(activeDateTime)
            }),
            takeUntil(this.onDestroy$),
            shareReplay(1)
        )

        this.venue$ = availability$.pipe(
            map(availability => availability.venue)
        )

        this.event$ = this.venue$.pipe(
            map(venue => venue?.eventWithId(this.eventId) ?? null)
        )

        this.eventBookingSlotGroups$ = availability$.pipe(
            map(availability => availability.bookingSlotTimeGroupsForEvent(this.eventId))
        )

        this.modalIdealTime$ = this.eventBookingSlotGroups$.pipe(
            map(groups => {
                const isSingleSlot = groups.length === 1 && groups[0].slotCount === 1
                if (isSingleSlot) {
                    return groups[0].start
                }
                return null
            })
        )
        const encodedAddress$ = this.venue$.pipe(
            map(venue => {
                if (!venue) {
                    return null
                }
                const address = venue.address
                return encodeURIComponent(
                    [address.addressLineOne, address.addressLineTwo, address.city, address.postCode, address.country]
                        .filter(Boolean)
                        .join(', ')
                )
            })
        )

        this.mapUrl$ = encodedAddress$.pipe(
            map(address => address
                ? `https://www.google.com/maps/embed/v1/place?key=${environment.googleMapsApiKey}&q=${address}`
                : null
            )
        )

        this.directionsUrl$ = encodedAddress$.pipe(
            map(address => address ? environment.getDirectionsURL + address : null)
        )
    }

    dateTimeSelected(value: string) {
        const selectedDateTime = this.eventDateOptions.find(dateTime => {
            return this.dateTimeSelectValue(dateTime) === value
        })
        if (!selectedDateTime) {
            return
        }
        this.updateDateTimeQueryParam(selectedDateTime, false)
    }

    dateTimeSelectValue(dateTime: Date | null): string {
        if (!dateTime) {
            return ''
        }
        return [
            dateTime.getFullYear().toString().padStart(4, '0'),
            (dateTime.getMonth() + 1).toString().padStart(2, '0'),
            dateTime.getDate().toString().padStart(2, '0'),
        ].join('-')
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    private getAvailabilityForDate(date: Date) {
        this.venueNotInService = false
        return this.availabilityService.getAvailability(
            this.organisationId,
            this.businessId,
            this.venueId,
            date,
            date
        )
            .pipe(
                catchError(error => {
                    if (isVenueNotInServiceError(error)) {
                        this.venueNotInService = true
                        return EMPTY
                    }
                    return throwError(() => error)
                })
            )
    }

    private updateDateTimeQueryParam(dateTime: Date, replaceUrl: boolean) {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { dateTime: dateTime.toISOString() },
            queryParamsHandling: 'merge',
            replaceUrl,
        })
    }

    private resolveActiveDateTime(requestedDateTime: Date | null, event: Event | null, referenceDate: Date): Date | null {
        const nextDateTime = event?.recurrence.nextDateTimeOnOrAfter(referenceDate) ?? null
        if (requestedDateTime === null) {
            return nextDateTime
        }
        if (requestedDateTime < referenceDate && nextDateTime !== null) {
            return nextDateTime
        }
        return requestedDateTime
    }

    private sameDateTime(first: Date | null, second: Date | null): boolean {
        return (first?.getTime() ?? null) === (second?.getTime() ?? null)
    }

    private sameLocalDate(first: Date, second: Date): boolean {
        return first.getFullYear() === second.getFullYear()
            && first.getMonth() === second.getMonth()
            && first.getDate() === second.getDate()
    }

    private includeActiveDateTimeOption(options: Date[], activeDateTime: Date | null): Date[] {
        if (!activeDateTime) {
            return options
        }
        if (options.some(option => this.sameLocalDate(option, activeDateTime))) {
            return options
        }
        return [...options, activeDateTime].sort((a, b) => a.getTime() - b.getTime())
    }

    protected readonly environment = environment
}
